<script>
import axios from 'axios';
import utilsService from '@/api/utilsService';
import router from '@/routes/router';

const apiUrl = `${process.env.VUE_APP_API_URL}/api/empresas`;

// 👉 Siempre usa token fresco
const getHeaders = () => ({
  'Authorization': `Bearer ${utilsService.getToken()}`,
  'Content-Type': 'application/json',
});

const empresaService = {
  async get(id) {
    try {
      const response = await axios.get(`${apiUrl}/${id}`, { headers: getHeaders() });
      if (!response) throw new Error('Sin respuesta');

      // Normaliza 0 → null para selects
      response.data.paisID   = response.data.paisID   == 0 ? null : response.data.paisID;
      response.data.regionID = response.data.regionID == 0 ? null : response.data.regionID;
      response.data.comunaID = response.data.comunaID == 0 ? null : response.data.comunaID;
      return response.data;
    } catch (error) {
      router.push('/error');
    }
  },

  // Lista de empresas según rol
  async getAll(userRole) {
    try {
      let endpoint = apiUrl;
      if (userRole === 'ventas inex') {
        endpoint = `${apiUrl}/usuario`;   // usuarios internos (admin/ventas)
      } else if (userRole === 'cliente') {
        endpoint = `${apiUrl}/cliente`;   // clientes (REL Cliente–Empresa)
      }

      console.log('Fetching empresas from:', endpoint);
      const { data } = await axios.get(endpoint, { headers: getHeaders() });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  // 🔹 Usuarios internos (extrae userId del token en backend)
async getEmpresasByUser() {
  try {
    const { data } = await axios.get(`${apiUrl}/usuario`, { headers: getHeaders() });
    console.log('Empresas (byUser):', Array.isArray(data) ? data : []);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const status = (error && error.response) ? error.response.status : undefined;
    const message = error && error.message ? error.message : String(error);
    console.error('Error al obtener empresas (byUser):', status, message);
    return [];
  }
},

// 🔹 Todas las empresas (admin / fallback ventas inex)
async getEmpresas() {
  try {
    // si apiUrl = '/api/empresas', esta llamada queda en '/api/empresas'
    const { data } = await axios.get(`${apiUrl}`, { headers: getHeaders() });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const status = (error && error.response) ? error.response.status : undefined;
    const message = error && error.message ? error.message : String(error);
    console.warn('Error al obtener empresas (ALL):', status, message);
    return [];
  }
},

  async getEmpresasByCliente() {
    try {
      const { data } = await axios.get(`${apiUrl}/usuario`, { headers: getHeaders() });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  async getUsersByEmpresa(id) {
    try {
      const { data } = await axios.get(`${apiUrl}/${id}/users`, { headers: getHeaders() });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  async getMaterialesByEmpresa(id) {
    try {
      const { data } = await axios.get(`${apiUrl}/${id}/materiales`, { headers: getHeaders() });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  async getEmpresasByUsuario(usuarioId) {
    try {
      const { data } = await axios.get(`${apiUrl}/check-usuario/${usuarioId}`, { headers: getHeaders() });
      return data;
    } catch (error) {
      console.error('Error al obtener empresas del usuario:', error.message);
      return [];
    }
  },

  async getEmpresasOptions() {
    try {
      const { data } = await axios.get(`${apiUrl}/options/empresas`, { headers: getHeaders() });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  async getSuscripcionesOptions() {
    try {
      const { data } = await axios.get(`${apiUrl}/options/suscripciones`, { headers: getHeaders() });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  async getIntereses(id, lang = 'es') {
    try {
      const { data } = await axios.get(`${apiUrl}/${id}/intereses`, {
        headers: getHeaders(),
        params: { lang }
      });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  
  async buscarEmpresas(query) {
    try {
      const q = encodeURIComponent(String(query || "").trim());
      if (!q || q.length < 2) return [];
      const { data } = await axios.get(`${apiUrl}/search`, {
        headers: getHeaders(),
        params: { q: query }
      });
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return [];
    }
  },

  async obtenerClientePorIdentificador(identificador) {
    try {
      const id = encodeURIComponent(String(identificador || "").trim());
      if (!id) {
        throw new Error("Identificador vacío");
      }

      const url = `${apiUrl}/rut/${id}`;
      //console.log("[obtenerClientePorIdentificador] GET:", url);
      const { data } = await axios.get(url, { headers: getHeaders() });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  async getChoferesByEmpresa(id) {
    try {
      const { data } = await axios.get(`${apiUrl}/${id}/choferes`, { headers: getHeaders() });
      return data;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  },

  async create(formRaw) {
    try {
      const idHolding = formRaw
        ? (formRaw.IdHolding != null ? formRaw.IdHolding : (formRaw.idHolding != null ? formRaw.idHolding : null))
        : null;

      const form = {
        ...formRaw,
        idPais: formRaw.idPais || 0,
        idRegion: formRaw.idRegion || 0,
        idComuna: formRaw.idComuna || 0,
        idIndustria: formRaw.idIndustria || 0,
        IdHolding: idHolding,
      };

      const { data } = await axios.post(`${apiUrl}/create`, form, { headers: getHeaders() });
      return data;
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data || 'Error al crear el ingreso.');
      } else {
        throw new Error(error.message || 'Error en la solicitud.');
      }
    }
  },

  async update(formRaw, id) {
    try {
      const idHolding = formRaw && formRaw.IdHolding !== undefined ? formRaw.IdHolding : null;

      const form = {
        ...formRaw,
        IdHolding: idHolding,
      };
      const { status } = await axios.put(`${apiUrl}/${id}`, form, { headers: getHeaders() });
      return status;
    } catch (error) {
      console.error('Error al enviar el formulario', error);
    }
  },

  async deleteBy(id) {
    try {
      const { status } = await axios.delete(`${apiUrl}/${id}`, { headers: getHeaders() });
      return status;
    } catch (error) {
      throw new Error('Error fetching data: ' + error.message);
    }
  }
};

export default empresaService;
</script>